import React, {ReactNode, useRef, useState} from 'react';
import { Product } from '../../model/Products';
import { Subscription } from '../../model/Subscription';
import { B2B2ProductDropdown } from './B2B2ProductDropdown';
import styled from 'styled-components';

interface Props {
	currentPrice: number;
	targetProducts: Product[];
	initialSelectedProduct: Product | undefined;
	subscription: Subscription;
	productDesc: ReactNode;
	setSelectedProduct: (product: Product) => void;
}

export const B2B2ChangeSubsRow: React.FC<Props> = (props: Props) => {

	const [isUpdated, setIsUpdated] = useState(false);

	const rowUpdated = () => {
		setIsUpdated(true);
		executeScroll();
	}

	const handleAnimationEnd = () => {
		setIsUpdated(false);
	}

	const componentRef = useRef<null | HTMLDivElement>(null);
	const executeScroll = () => {
		if (componentRef != null) {
			componentRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	return (
		<SubsRow ref={componentRef as any}>
			<CurrentSubs>
				<div className="subs-info">
					<div className="subs-title">Vanha liittymä:</div>
					<span className="subs-name">{props.subscription.name}</span> [{props.subscription.msisdn}]
					<div className="subs-details">{props.productDesc}</div>
					{props.subscription.needsActivation &&
						<div className="subs-needs-activation">Numeron siirto kesken</div>
					}
				</div>
				<div className="subs-price">{props.currentPrice}€/kk</div>
			</CurrentSubs>
			<NewSubs className={isUpdated ? 'updated' : ''} onAnimationEnd={handleAnimationEnd}>
				<div className="subs-info">
					<div className="subs-title">Uusi liittymä:</div>
					<B2B2ProductDropdown
						rowUpdated={rowUpdated}
						initialSelectedProduct={props.initialSelectedProduct}
						targetProducts={props.targetProducts}
						setSelectedProduct={props.setSelectedProduct} />
				</div>
			</NewSubs>
		</SubsRow>
	);
};

const SubsRow = styled.div`
	background-color: #fff;
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.16);
	border-radius: 8px;
	margin-bottom: 1rem;
	scroll-margin: 20px;

	@media screen and (min-width: 800px) {
		display: flex;
		margin-bottom: 0.5rem;
	}
`;

const CurrentSubs = styled.div`
	position: relative;
	border-radius: 8px;
	padding: 10px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;

	&:after {
		top: 100%;
		left: 50%;
		border: solid transparent;
		content: '';
		height: 0;
		width: 0;
		position: absolute;
		pointer-events: none;
		border-left: 16px solid transparent;
		border-right: 16px solid transparent;
		border-top: 11px solid #fff;
		margin-left: -8px;
	}

	.subs-info {
		flex: 0 1 100%;
		font-weight: bold;
		
		>.subs-name {
			font-weight: 500;
		}

		> .subs-title {
			color: #ff0000;
		}
		
		> .subs-details {
			font-weight: bold;
			text-decoration: line-through;
		}
		> .subs-needs-activation {
			color: #ff0000;
			font-weight: bold;
			font-size: 16px;
		}
	}

	.subs-price {
		font-weight: bold;
		text-decoration: line-through;
		width: 80px;
		text-align: right;
	}

	@media screen and (min-width: 800px) {
		width: 50%;
		padding: 12px 0 12px 12px;

		&:after {
			content: '';
			position: absolute;
			z-index: 10;
			width: 20px;
			bottom: 0px;
			top: 0px;
			right: -20px;
			background-image: linear-gradient(to top right, #ffffff 50%, transparent 51%), linear-gradient(to bottom right, #ffffff 50%, transparent 51%);
			background-size: 100% 50%;
			background-position: top left, bottom left;
			background-repeat: no-repeat;
			height: auto;
			margin: 0;
			border: none;
			left: auto;
		}

		.subs-info {
			> .subs-title {
				display: none;
			}
		}
	}
`;

const NewSubs = styled.div`
	background-color: #99cc00;
	border-radius: 0 0 8px 8px;
	padding: 10px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	
	@keyframes blink {
	  	from {
			background: #99cc00;
	  	}
	  	50% {
			background: #A6DC03;
	  	}
	  	to {
			background: #99cc00;
	  	}
	}
	
	&.updated {
		animation: blink .75s ease 0s;
		animation-iteration-count: 2;
	}

	.subs-info {
		flex: 0 1 100%;

		> .subs-title {
			color: #fff;
			font-weight: bold;
			margin-bottom: 5px;
		}
	}

	@media screen and (min-width: 800px) {
		width: 50%;
		border-radius: 0 8px 8px 0;
		padding: 12px 12px 12px 36px;

		.subs-info {
			> .subs-title {
				display: none;
			}
		}
	}
`;
